import React, { useRef, useState } from 'react';
import { useTranslation } from '../../../i18n';
import { compressImageForUpload } from '../../../utils/compressImage';

export interface HouseRunPhotoSubmitProps {
  disabled?: boolean;
  uploading?: boolean;
  onSubmit: (photoData: string) => void;
}

export const HouseRunPhotoSubmit: React.FC<HouseRunPhotoSubmitProps> = ({
  disabled,
  uploading,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);

  const handleFile = async (file: File) => {
    setError(null);
    try {
      const dataUrl = await compressImageForUpload(file);
      setPreview(dataUrl);
    } catch {
      setError(t('gameplay.houseRun.photoTooLarge'));
    }
  };

  const handleConfirm = () => {
    if (preview) onSubmit(preview);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', width: '100%' }}>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void handleFile(file);
          e.target.value = '';
        }}
      />

      {!preview ? (
        <button
          type="button"
          disabled={disabled || uploading}
          onClick={() => inputRef.current?.click()}
          style={{
            padding: '1rem',
            background: 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)',
            border: 'none',
            borderRadius: '0.75rem',
            color: 'white',
            fontSize: '1rem',
            fontWeight: 700,
            cursor: disabled || uploading ? 'not-allowed' : 'pointer',
            opacity: disabled || uploading ? 0.5 : 1,
          }}
        >
          {t('gameplay.houseRun.takePhoto')}
        </button>
      ) : (
        <>
          <img
            src={preview}
            alt=""
            style={{
              width: '100%',
              maxHeight: '240px',
              objectFit: 'cover',
              borderRadius: '0.75rem',
              border: '2px solid rgba(139, 92, 246, 0.4)',
            }}
          />
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              type="button"
              disabled={uploading}
              onClick={() => setPreview(null)}
              style={{
                flex: 1,
                padding: '0.75rem',
                background: 'rgba(255,255,255,0.1)',
                border: 'none',
                borderRadius: '0.5rem',
                color: 'white',
                cursor: uploading ? 'not-allowed' : 'pointer',
              }}
            >
              {t('gameplay.houseRun.retake')}
            </button>
            <button
              type="button"
              disabled={uploading}
              onClick={handleConfirm}
              style={{
                flex: 2,
                padding: '0.75rem',
                background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
                border: 'none',
                borderRadius: '0.5rem',
                color: 'white',
                fontWeight: 700,
                cursor: uploading ? 'not-allowed' : 'pointer',
              }}
            >
              {uploading ? t('gameplay.houseRun.uploading') : t('gameplay.houseRun.submitPhoto')}
            </button>
          </div>
        </>
      )}

      {error && (
        <p style={{ color: '#F87171', fontSize: '0.85rem', margin: 0, textAlign: 'center' }}>{error}</p>
      )}
    </div>
  );
};
